'use client'

import { ReactNode, useEffect, useLayoutEffect, useState } from 'react'
import { useWeb3Ready } from '@domain/auth/context/Web3ReadyContext'

/**
 * Loads the heavy Web3 chunk (Privy, Wagmi, etc.) on demand — only when
 * web3Ready.activate() is called (e.g. login button click or Web3-heavy page mount).
 * Marketing pages never load Web3 unless the user interacts, keeping TBT and LCP clean.
 *
 * Animation guard: when Web3 providers mount they cause React to remount children
 * (Fragment → Web3Chunk type change), which would replay CSS entrance animations.
 * We suppress this by setting `data-no-animate` on <html> before the state update,
 * then immediately marking already-animated elements with `animation-done` so they
 * stay visible on any subsequent remount while still animating fresh on page navigation.
 */
export function DeferredWeb3Wrapper({ children }: { children: ReactNode }) {
  const [Web3Chunk, setWeb3Chunk] = useState<React.ComponentType<{
    children: ReactNode
  }> | null>(null)
  const web3Ready = useWeb3Ready()

  useEffect(() => {
    web3Ready?._registerActivate(() => {
      import('../Web3ProvidersHeavy').then((m) => {
        document.documentElement.setAttribute('data-no-animate', 'true')
        setWeb3Chunk(() => m.default)
      })
    })

    // Privy OAuth callbacks (Google, etc.) redirect back to the app with these params.
    // Privy must be loaded immediately to complete the token exchange — don't wait for user action.
    if (window.location.search.includes('privy_oauth_')) {
      web3Ready?.activate()
    }

    // #login hash — activate Web3 immediately so PrivyAuthButtonProvider mounts
    // and can open the login modal once Privy is ready.
    if (window.location.hash === '#login') {
      web3Ready?.activate()
    }
  }, [web3Ready])

  // Fires synchronously after React commits the remount — before the browser paints.
  // Mark newly mounted elements so they won't re-animate, then lift the suppression.
  useLayoutEffect(() => {
    if (!Web3Chunk) return
    document.querySelectorAll<HTMLElement>('.animate-fade-up').forEach((el) => {
      el.classList.add('animation-done')
    })
    document.documentElement.removeAttribute('data-no-animate')
  }, [Web3Chunk])

  if (!Web3Chunk) {
    return <>{children}</>
  }

  return <Web3Chunk>{children}</Web3Chunk>
}
